import React, { useState } from 'react';
import type {
  SelectChangeEvent} from '@mui/material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import SchoolIcon from '@mui/icons-material/School';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { lessonService } from '../services/lessonService';
import { useAuthStore } from '../store/authStore';
import type { CEFRLevel, Lesson, LessonStatus } from '../types';

const CEFR_LEVELS: CEFRLevel[] = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
const STATUS_COLORS: Record<LessonStatus, 'default' | 'warning' | 'info' | 'success'> = {
  draft: 'default',
  in_review: 'warning',
  approved: 'info',
  published: 'success',
};

const createLessonSchema = z.object({
  title: z.string().min(3, 'Title must be at least 3 characters'),
  cefr_level: z.enum(['A1', 'A2', 'B1', 'B2', 'C1', 'C2']),
  topic: z.string().min(3, 'Topic is required'),
  duration_min: z.coerce.number().min(5).max(300),
});
type CreateLessonForm = z.infer<typeof createLessonSchema>;

const CEFR_STRIPE_COLORS: Record<CEFRLevel, string> = {
  A1: 'success.light',
  A2: 'success.light',
  B1: 'primary.light',
  B2: 'primary.light',
  C1: 'secondary.light',
  C2: 'secondary.light',
};

export const AuthorDashboard: React.FC = () => {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [cefrFilter, setCefrFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Lesson | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['lessons', { page, statusFilter, cefrFilter, searchQuery, authorId: user?.id }],
    queryFn: () =>
      lessonService.getAll({
        page,
        per_page: 12,
        status: statusFilter || undefined,
        cefr_level: cefrFilter || undefined,
        search: searchQuery || undefined,
        author_id: user?.id,
      }),
  });

  const lessons = data?.data ?? [];
  const total = data?.meta?.total ?? 0;
  const perPage = data?.meta?.per_page ?? 12;
  const pageCount = Math.ceil(total / perPage);

  const stats = {
    total: total,
    drafts: lessons.filter((l) => l.status === 'draft').length,
    in_review: lessons.filter((l) => l.status === 'in_review').length,
    published: lessons.filter((l) => l.status === 'published').length,
  };

  const {
    control,
    handleSubmit,
    reset: resetForm,
    formState: { errors },
  } = useForm<CreateLessonForm>({
    resolver: zodResolver(createLessonSchema),
    defaultValues: { title: '', cefr_level: 'B1', topic: '', duration_min: 60 },
  });

  const createMutation = useMutation({
    mutationFn: lessonService.create,
    onSuccess: (lesson) => {
      queryClient.invalidateQueries({ queryKey: ['lessons'] });
      setCreateOpen(false);
      resetForm();
      navigate(`/lessons/${lesson.id}/edit`);
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: lessonService.duplicate,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['lessons'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: lessonService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lessons'] });
      setDeleteTarget(null);
    },
  });

  const onCreateSubmit = (formData: CreateLessonForm) => {
    createMutation.mutate(formData);
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', fontFamily: '"Noto Sans", sans-serif' }}>
      {/* Hero header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #003359 0%, #0A64C9 100%)',
          p: 3,
          borderRadius: 0,
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700} color="white">
            Good morning, {user?.name}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>
            Here's your lesson overview
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          sx={{
            bgcolor: 'white',
            color: 'primary.main',
            fontWeight: 700,
            borderRadius: 2,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
          }}
        >
          New Lesson
        </Button>
      </Box>

      {/* Stat cards — overlapping the hero with negative margin */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          flexWrap: 'wrap',
          mt: -3,
          px: 3,
        }}
      >
        {[
          { label: 'Total Lessons', value: total, color: 'primary.main' },
          { label: 'Drafts', value: stats.drafts, color: 'grey.500' },
          { label: 'In Review', value: stats.in_review, color: 'warning.main' },
          { label: 'Published', value: stats.published, color: 'success.main' },
        ].map(({ label, value, color }) => (
          <Paper
            key={label}
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              p: 2.5,
              flex: 1,
              minWidth: 140,
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'background.paper',
            }}
          >
            <Box
              sx={{
                width: 3,
                borderRadius: 4,
                bgcolor: color,
                height: 40,
                mr: 2,
                flexShrink: 0,
              }}
            />
            <Box>
              <Typography variant="h4" fontWeight={800} color={color} lineHeight={1}>
                {value}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {label}
              </Typography>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Filter row */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ px: 3, mt: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search lessons…"
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
          sx={{ flex: 1 }}
        />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e: SelectChangeEvent) => { setStatusFilter(e.target.value); setPage(1); }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="in_review">In Review</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="published">Published</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>CEFR Level</InputLabel>
          <Select
            label="CEFR Level"
            value={cefrFilter}
            onChange={(e: SelectChangeEvent) => { setCefrFilter(e.target.value); setPage(1); }}
          >
            <MenuItem value="">All</MenuItem>
            {CEFR_LEVELS.map((l) => (
              <MenuItem key={l} value={l}>{l}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {isError && (
        <Box sx={{ px: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load lessons. Please try again.
          </Alert>
        </Box>
      )}

      {/* Lesson grid */}
      {isLoading ? (
        <Grid container spacing={2.5} sx={{ px: 3 }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Grid item key={i} xs={12} sm={6} md={4}>
              <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      ) : lessons.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 10, color: 'text.disabled' }}>
          <SchoolIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.disabled">
            No lessons yet
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
            Create your first lesson to get started.
          </Typography>
          <Button variant="contained" onClick={() => setCreateOpen(true)}>
            Create New Lesson
          </Button>
        </Box>
      ) : (
        <Grid container spacing={2.5} sx={{ px: 3 }}>
          {lessons.map((lesson) => (
            <Grid item key={lesson.id} xs={12} sm={6} md={4}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s ease',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                {/* Colored CEFR stripe */}
                <Box
                  sx={{
                    height: 4,
                    bgcolor: CEFR_STRIPE_COLORS[lesson.cefr_level] ?? 'primary.light',
                  }}
                />
                <CardContent sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                    <Chip label={lesson.cefr_level} size="small" color="primary" />
                    <Chip
                      label={lesson.status.replace('_', ' ')}
                      size="small"
                      color={STATUS_COLORS[lesson.status]}
                    />
                  </Box>
                  <Typography variant="h6" fontWeight={700} noWrap sx={{ mb: 0.5 }}>
                    {lesson.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap sx={{ mb: 0.5 }}>
                    {lesson.topic}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {lesson.duration_min} min &bull; Updated{' '}
                    {new Date(lesson.updated_at).toLocaleDateString()}
                  </Typography>
                </CardContent>
                <CardActions sx={{ px: 2, pb: 2, gap: 0.5 }}>
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => navigate(`/lessons/${lesson.id}/edit`)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Duplicate">
                    <IconButton
                      size="small"
                      onClick={() => duplicateMutation.mutate(lesson.id)}
                      disabled={duplicateMutation.isPending}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setDeleteTarget(lesson)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, pb: 4 }}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, p) => setPage(p)}
            color="primary"
          />
        </Box>
      )}

      {/* Create Lesson Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              background: 'linear-gradient(135deg, #003359 0%, #0A64C9 100%)',
              p: 2.5,
              color: 'white',
            }}
          >
            <Typography variant="h6" fontWeight={700} color="white">
              Create New Lesson
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box
            component="form"
            id="create-lesson-form"
            onSubmit={handleSubmit(onCreateSubmit)}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}
          >
            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Lesson Title"
                  fullWidth
                  error={!!errors.title}
                  helperText={errors.title?.message}
                />
              )}
            />
            <Controller
              name="cefr_level"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.cefr_level}>
                  <InputLabel>CEFR Level</InputLabel>
                  <Select {...field} label="CEFR Level">
                    {CEFR_LEVELS.map((l) => (
                      <MenuItem key={l} value={l}>{l}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
            <Controller
              name="topic"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Topic"
                  fullWidth
                  error={!!errors.topic}
                  helperText={errors.topic?.message}
                />
              )}
            />
            <Controller
              name="duration_min"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Duration (minutes)"
                  type="number"
                  fullWidth
                  error={!!errors.duration_min}
                  helperText={errors.duration_min?.message}
                />
              )}
            />
            {createMutation.isError && (
              <Alert severity="error">Failed to create lesson. Please try again.</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            type="submit"
            form="create-lesson-form"
            variant="contained"
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Lesson</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{deleteTarget?.title}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
